import moment from 'moment';
import { keyBy as _keyBy, last as _last, groupBy as _groupBy, isNumber as _isNumber } from 'lodash';
import type { Dictionary } from 'lodash';
import { datetime, Driver, PickupDropoffOption, TaskTypeId, OPTIMIZATION_TYPE } from '@bringg/types';
import type { GanttTimeLineItem, TimelineItemValuesHistory } from '@bringg-frontend/gantt-timeline';
import type { MapboxMarkerData } from '@bringg/react-components/dist/components/mapbox/mapbox';
import type { Directions } from '@bringg/types/types/directions';
import { decodePath } from '@bringg-frontend/eta-calculator';

import { NO_ROUTE_ID } from '../../stores/routes-planner-store';
import { hexToRGB } from '../../utils/color-utils';
import { isTaskStarted } from '../../utils/bringgTypesTransformers';
import type { RoutesPlannerRun, RoutesPlannerTask } from '../../types';

export const getMarkers = (sortedTasks, groupId, groupColor, markerIconData, inProgressGroups): MapboxMarkerData[] => {
	return sortedTasks.map(task => {
		const taskStatus = task.status;
		let color = groupColor;

		if (isTaskStarted(taskStatus) || inProgressGroups.has(groupId)) {
			color = hexToRGB(color, 0.5);
		}

		return {
			id: `${groupId}_${task.way_points[0].id}_${task.id}`,
			groupId,
			text: isTaskAssigned(groupId) ? task.priority.toString() : '\u0058',
			color,
			icon: markerIconData.icon,
			textColor: 'rgb(255, 255, 255)',
			iconOptions: markerIconData.iconOptions,
			location: {
				lat: task.way_points[0].lat,
				lng: task.way_points[0].lng
			}
		} as MapboxMarkerData;
	});
};

export const isTaskAssigned = (runId: number) => runId > NO_ROUTE_ID;

export const isRouteExistsAndUnassigned = (run: RoutesPlannerRun, optimizationType: OPTIMIZATION_TYPE) => {
	if (!run) {
		return false;
	}

	return !isRouteAssigned(run, optimizationType);
};

export const isRouteAssigned = (run: RoutesPlannerRun, optimizationType: OPTIMIZATION_TYPE): boolean => {
	if (!run) {
		return false;
	}

	return !!run.user_id || (optimizationType === OPTIMIZATION_TYPE.COMBINED && !!run.vehicle_id);
};

export const getPath = (groupDirections: Directions): Bringg.Location[] => {
	return (
		groupDirections?.directions.flatMap(directionAsString => {
			return decodePath(directionAsString);
		}) || []
	);
};

export const convertUnixSecondsToISO = (unixSeconds: number): datetime => {
	return moment.unix(unixSeconds).toISOString();
};

export const isItemGroupChanged = (item: GanttTimeLineItem, valuesHistory: TimelineItemValuesHistory): boolean => {
	const currentGroupId = item.rowId;
	const previousGroupId = valuesHistory.previousGroupId;

	return previousGroupId !== currentGroupId;
};

export const createDriverByIdMap = (drivers: Driver[]) => _keyBy(drivers, driver => driver.id);

export const createDriverByRunIdMap = (
	driversById: Dictionary<Driver>,
	tasks: Map<number, RoutesPlannerTask>
): Dictionary<Driver> => {
	const runToDriver: Dictionary<Driver> = {};
	tasks.forEach(task => {
		runToDriver[task.run_id] = driversById[task.user_id];
	});

	return runToDriver;
};

export const createTasksByRunIdMap = (tasks: RoutesPlannerTask[]): Dictionary<RoutesPlannerTask[]> => {
	return _groupBy(tasks, task => task.run_id);
};

export const createDirectionsByRunIdMap = (directions: Directions[]): Dictionary<Directions> => {
	return _keyBy(directions, item => item.run_id);
};

export const getActionableWayPoint = (task: RoutesPlannerTask) => {
	let activePickupDropOff: PickupDropoffOption;

	const wayPoints = task.way_points.filter(wp => !wp.hidden);

	switch (task.task_type_id) {
		case TaskTypeId.PICKUP:
			activePickupDropOff = PickupDropoffOption.PICKUP;
			break;
		case TaskTypeId.DELIVERY:
			activePickupDropOff = PickupDropoffOption.DROPOFF;
			break;
		case TaskTypeId.PICKUP_AND_DELIVERY:
			activePickupDropOff = PickupDropoffOption.BOTH;
			break;
	}

	const actionableWayPoint = wayPoints.find(
		wp => _isNumber(activePickupDropOff) && wp.pickup_dropoff_option === activePickupDropOff
	);

	return actionableWayPoint || _last(wayPoints);
};
